import api from '@/shared/api/api';
import {
  Receivable,
  PaginatedReceivablesResponse,
  GetReceivablesParams,
  CreateReceivablePayload,
  UpdateReceivablePayload,
  ReceivableDocument,
  CreateReceivableDocumentPayload,
  CreateReceivableDocumentApiResponse,
  UpdateReceivableDocumentPayload,
  ReceivableDocumentType,
  ReceivableTax,
  CreateReceivableTaxPayload,
  UpdateReceivableTaxPayload,
  ReceivableTaxDocument,
  CreateReceivableTaxDocumentPayload,
  CreateReceivableTaxDocumentApiResponse,
  PaginatedReceivableTaxesResponse,
  ReceivableTaxDocumentType,
} from '../utils/types';

/** Envia o arquivo para a URL pré-assinada usando o token retornado pelo POST (não o token de sessão). */
async function uploadFileToSignedUrl(signedUploadUrl: string, token: string, file: File): Promise<void> {
  const contentType = file.type || 'application/octet-stream';
  const response = await fetch(signedUploadUrl, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': contentType,
    },
    body: file,
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Upload falhou: ${response.status}`);
  }
}

const buildTaxPayload = (payload: {
  receivableId?: string;
  currency?: string;
  type?: string;
  description?: string;
  document?: string;
  value?: number;
}) => {
  const body: Record<string, unknown> = {};
  if (payload.receivableId) body.receivableId = payload.receivableId;
  if (payload.currency) body.currency = payload.currency;
  if (payload.type) body.type = payload.type;
  if (payload.description !== undefined) body.description = payload.description;
  if (payload.document !== undefined) body.document = payload.document;
  if (payload.value !== undefined) body.value = payload.value;
  return body;
};

export const receivableApi = {
  list: async (
    params?: GetReceivablesParams,
    signal?: AbortSignal
  ): Promise<PaginatedReceivablesResponse> => {
    // Mantém camelCase para os parâmetros
    const mapped: Record<string, unknown> = {};
    const p = params ?? {};
    if (p.page !== undefined) mapped.page = p.page;
    if (p.limit !== undefined) mapped.limit = p.limit;
    if (p.status && p.status !== 'all') mapped.status = p.status;
    if (p.store && p.store !== 'all') mapped.store = p.store;
    if (p.relatedDateStart) mapped.relatedDateStart = p.relatedDateStart;
    if (p.relatedDateEnd) mapped.relatedDateEnd = p.relatedDateEnd;
    if (p.paidDateStart) mapped.paidDateStart = p.paidDateStart;
    if (p.paidDateEnd) mapped.paidDateEnd = p.paidDateEnd;
    // if (p.shareConfigId) mapped.receivableShareConfigId = p.shareConfigId;
    // if (p.createdBy) mapped.createdBy = p.createdBy;
    if (p.search) mapped.search = p.search;
    return await api<PaginatedReceivablesResponse>({
      url: '/v1/receivables',
      method: 'GET',
      params: mapped,
      signal,
    });
  },

  get: async (id: string, signal?: AbortSignal): Promise<Receivable> => {
    return api<Receivable>({
      url: `/v1/receivables/${id}`,
      method: 'GET',
      signal,
    });
  },

  create: async (payload: CreateReceivablePayload): Promise<Receivable> => {
    return api<Receivable>({
      url: '/v1/receivables',
      method: 'POST',
      data: payload,
    });
  },

  update: async (id: string, data: UpdateReceivablePayload): Promise<Receivable> => {

    return api<Receivable>({
      url: `/v1/receivables/${id}`,
      method: 'PATCH',
      data,
    });
  },

  remove: async (id: string): Promise<{ id: string; deletedAt: string }> => {
    return api({
      url: `/v1/receivables/${id}`,
      method: 'DELETE',
    });
  },

  documents: {
    list: async (receivableId: string, signal?: AbortSignal): Promise<ReceivableDocument[]> => {
      return api<ReceivableDocument[]>({
        url: `/v1/receivables/${receivableId}/documents`,
        method: 'GET',
        signal,
      });
    },

    /**
     * Obtém a URL pré-assinada chamando POST /v1/receivables/:receivableId/documents
     * (receivable id na URL). Para enviar vários arquivos, chame create uma vez por arquivo
     * ou use createMany.
     */
    create: async (payload: CreateReceivableDocumentPayload): Promise<ReceivableDocument> => {
      const body: { documentType: ReceivableDocumentType; description?: string } = {
        documentType: payload.documentType,
      };
      if (payload.description !== undefined && payload.description !== '') {
        body.description = payload.description;
      }
      const res = await api<CreateReceivableDocumentApiResponse>({
        url: `/v1/receivables/${payload.receivableId}/documents`,
        method: 'POST',
        data: body,
      });
      if (payload.file) {
        await uploadFileToSignedUrl(res.signedUploadUrl, res.token, payload.file);
      }
      return {
        id: res.documentId,
        receivableId: payload.receivableId,
        documentUrl: res.publicUrl,
        documentType: res.documentType,
        description: res.description ?? undefined,
        createdAt: new Date().toISOString(),
      };
    },

    /**
     * Envia vários documentos: para cada item chama POST .../documents para obter
     * a URL pré-assinada e em seguida faz PUT do arquivo nessa URL.
     */
    createMany: async (
      receivableId: string,
      items: Array<{ file: File; documentType: ReceivableDocumentType; description?: string }>
    ): Promise<ReceivableDocument[]> => {
      const results: ReceivableDocument[] = [];
      for (const item of items) {
        const doc = await receivableApi.documents.create({
          receivableId,
          documentType: item.documentType,
          description: item.description,
          file: item.file,
        });
        results.push(doc);
      }
      return results;
    },

    update: async (payload: UpdateReceivableDocumentPayload): Promise<ReceivableDocument> => {
      const { id, receivableId, documentType, description } = payload;
      if (!receivableId) throw new Error('receivableId é obrigatório');
      const data: Record<string, unknown> = {};
      if (documentType !== undefined) data.documentType = documentType;
      if (description !== undefined) data.description = description;
      return api<ReceivableDocument>({
        url: `/v1/receivables/${receivableId}/documents/${id}`,
        method: 'PATCH',
        data,
      });
    },

    remove: async (receivableId: string, id: string): Promise<{ receivableId: string; id: string; deletedAt?: string }> => {
      return api({
        url: `/v1/receivables/${receivableId}/documents/${id}`,
        method: 'DELETE',
      });
    },
  },

  taxes: {
    list: async (receivableId: string, search?: string, signal?: AbortSignal): Promise<ReceivableTax[]> => {
      const { data } = await api<PaginatedReceivableTaxesResponse>({
        url: '/v1/receivables-taxes',
        method: 'GET',
        params: { receivableId, search },
        signal,
      });
      return data;
    },

    create: async (payload: CreateReceivableTaxPayload): Promise<ReceivableTax> => {
      const data = buildTaxPayload({
        receivableId: payload.receivableId,
        currency: payload.currency,
        type: payload.type,
        description: payload.description,
        document: payload.document,
        value: payload.value,
      });
      return api<ReceivableTax>({
        url: '/v1/receivables-taxes',
        method: 'POST',
        data,
      });
    },

    update: async (payload: UpdateReceivableTaxPayload): Promise<ReceivableTax> => {
      const { id, ...rest } = payload;
      const data = buildTaxPayload(rest);
      return api<ReceivableTax>({
        url: `/v1/receivables-taxes/${id}`,
        method: 'PUT',
        data,
      });
    },

    remove: async (id: string): Promise<{ id: string; deletedAt?: string }> => {
      return api({
        url: `/v1/receivables-taxes/${id}`,
        method: 'DELETE',
      });
    },

    documents: {
      list: async (taxId: string, signal?: AbortSignal): Promise<ReceivableTaxDocument[]> => {
        return api<ReceivableTaxDocument[]>({
          url: `/v1/receivables-taxes/${taxId}/documents`,
          method: 'GET',
          signal,
        });
      },

      create: async (payload: CreateReceivableTaxDocumentPayload): Promise<ReceivableTaxDocument> => {
        const body: { documentType: ReceivableTaxDocumentType; description?: string } = {
          documentType: payload.documentType,
        };
        if (payload.description !== undefined && payload.description !== '') {
          body.description = payload.description;
        }
        const res = await api<CreateReceivableTaxDocumentApiResponse>({
          url: `/v1/receivables-taxes/${payload.receivableTaxId}/documents`,
          method: 'POST',
          data: body,
        });
        if (payload.file) {
          await uploadFileToSignedUrl(res.signedUploadUrl, res.token, payload.file);
        }
        return {
          id: res.documentId,
          receivableTaxId: payload.receivableTaxId,
          documentUrl: res.publicUrl,
          documentType: res.documentType,
          description: res.description ?? undefined,
          createdAt: new Date().toISOString(),
        };
      },

      remove: async ({ id, receivableId }: { id: string; receivableId: string }): Promise<{ id: string; deletedAt?: string }> => {
        return api({
          url: `/v1/receivables-taxes/${receivableId}/documents/${id}`,
          method: 'DELETE',
        });
      },
    },
  },
};
